<template>
  <v-container fluid>
    <!-- Breadcrumb + Título + Botón Volver -->
    <v-row align="center" justify="start">
      <v-col cols="12" md="4">
        <v-btn text color="primary" @click="volverAlListado">
          <v-icon left>mdi-arrow-left</v-icon>
          Volver al Listado
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <!-- 📌 Columna Izquierda: Entrada principal -->
      <v-col cols="12" md="4">
        <v-card>
          <v-card-title>
            <h3 class="text-h6 font-weight-bold">Datos de la Factura</h3>
          </v-card-title>
          <v-card-text>
            <DatePickerDate v-model="fechaContable" label="Fecha contable" />

            <v-text-field
              outlined
              label="Nº de factura"
              dense
              type="number"
              v-model="numeroFactura"
              v-validate="'numeric|max:13|min:1'"
              :error-messages="errors.collect('numeroFactura')"
              data-vv-name="numeroFactura"
              :counter="13"
              oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
              maxlength="13"
            ></v-text-field>
            <v-text-field
              outlined
              dense
              label="Sucursal"
              v-validate="'numeric|max:13|min:1'"
              :error-messages="errors.collect('sucursal')"
              data-vv-name="sucursal"
              v-model="sucursal"
              type="number"
              oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
              maxlength="5"
              :counter="5"
            ></v-text-field>
            <v-card outlined class="pa-4 mb-4">
              <h4 class="subtitle-1 font-weight-bold mb-2">Proveedor</h4>

              <ProveedorCardSelect
                :proveedorSeleccionado="proveedorSeleccionado"
                @set-proveedor-register="setProveedor"
              ></ProveedorCardSelect>
            </v-card>

            <v-card outlined class="pa-4 mb-4">
              <div class="subtitle-1 font-weight-bold mb-2">Totales IVA</div>
              <TotalesIvaFields :totalesIva.sync="totalesIva" />
            </v-card>

            <v-card outlined class="pa-4 mb-4">
              <div class="subtitle-1 font-weight-bold mb-2">
                Otros Impuestos
              </div>
              <ImpuestosAdicionalesFields
                :percepcionIva.sync="percepcionIva"
                :percepcionIB.sync="percepcionIB"
                :netoNoGravado.sync="netoNoGravado"
              />
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- 📌 Columna Central: Detalle de la Factura -->
      <v-col cols="12" md="8">
        <v-card outlined class="pa-6 mb-6 paper-preview">
          <!-- Cabecera tipo membrete -->
          <div class="factura-header mb-4">
            <h2 class="text-h5 font-weight-bold mb-2">Factura</h2>
            <div class="text-body-2 grey--text">
              Vista previa del comprobante de shared
            </div>
          </div>

          <!-- Datos principales -->
          <v-row class="factura-datos mb-6">
            <v-col cols="4">
              <div class="text-caption grey--text">Fecha</div>
              <div class="text-body-1">{{ fechaContableFormatted }}</div>
            </v-col>
            <v-col cols="4">
              <div class="text-caption grey--text">N° Factura</div>
              <div class="text-body-1">
                {{ numeroFactura || "No asignado" }}
              </div>
            </v-col>
            <v-col cols="4">
              <div class="text-caption grey--text">Sucursal</div>
              <div class="text-body-1">{{ sucursal || "No asignada" }}</div>
            </v-col>
          </v-row>

          <!-- Línea separadora -->
          <v-divider class="mb-6"></v-divider>

          <!-- Detalles de productos -->
          <div>
            <div class="d-flex align-center justify-space-between mb-2">
              <h3 class="subtitle-1 font-weight-bold">Detalle de Productos</h3>
              <div>
                <CompraDesdeArchivo
                  v-if="puedeUsarAsistente"
                  :sucursalUuid="uuidSucursal"
                  @aplicar="aplicarLectura"
                />
                <v-btn small text color="primary" @click="abrirDialogProducto">
                  <v-icon left small>mdi-plus</v-icon>
                  Agregar (F6)
                </v-btn>
              </div>
            </div>
            <v-alert v-if="precarga && precarga.pendientes.length" type="warning" dense text class="text-caption">
              {{ precarga.pendientes.length }} línea(s) de la factura sin producto asignado, agregalas con F6:
              <span v-for="(l, i) in precarga.pendientes" :key="i"><b>{{ l.descripcion }}</b> ({{ l.cantidad }} × {{ l.precio_unitario }}){{ i < precarga.pendientes.length - 1 ? ", " : "" }}</span>
            </v-alert>

            <FacturaCompraDetalleList
              :edit="true"
              :withIva="true"
              :detalles="detallesFactura"
            />

            <v-dialog
              persistent
              width="1000px"
              v-if="dialogProductoSelector"
              v-model="dialogProductoSelector"
            >
              <v-card class="rounded-lg">
                <v-card-title class="headline">
                  <v-icon left>mdi-package-variant-closed</v-icon>
                  Buscar Producto para Compra
                  <v-spacer></v-spacer>
                  <v-btn icon @click="dialogProductoSelector = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-container class="pa-4">
                  <ProductoSelector
                    priceField="precioCosto"
                    :showSuggestions="false"
                    :isCompra="true"
                    @process-producto="agregarProductoDetalle"
                    @close-dialog="dialogProductoSelector = false"
                  />
                </v-container>
              </v-card>
            </v-dialog>
          </div>

          <!-- Subtotales -->
          <v-divider class="mt-6 mb-4"></v-divider>
          <FacturaCompraSubtotal
            :totalesIva="totalesIva"
            :detalles="detallesFactura"
            :percepcionIva="percepcionIVANumber"
            :percepcionIB="percepcionIBNumber"
            :noGravado="netoNoGravadoNumber"
            :withIva="false"
          ></FacturaCompraSubtotal>

          <!-- Acciones -->
          <v-row class="mt-6">
            <v-col cols="12" md="6">
              <v-btn
                color="success"
                block
                :loading="loadingButton"
                :disabled="
                  loadingButton ||
                  !proveedorSeleccionado ||
                  detallesFactura.length === 0
                "
                @click="guardarFactura"
              >
                Guardar Factura
                <v-chip
                  x-small
                  outlined
                  color="white"
                  class="ml-2 font-weight-bold"
                >
                  F9
                </v-chip>
              </v-btn>
            </v-col>
            <v-col cols="12" md="6">
              <v-btn block @click="resetFormulario">Cancelar</v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <!-- 📌 Columna Derecha: Totales y Guardar -->
    </v-row>
  </v-container>
</template>

<script>
import DetalleComprobanteService from "@/services/comprobantes/DetalleComprobanteService";
import { COMPROBANTES } from "@/domain/comprobantes/config";
import ProductoSelector from "@/components/shared/comprobantes/detalles/ProductoSelector.vue";

import ProveedorCardSelect from "@/components/proveedor/ProveedorCardSelect";
import ProductoSucursalFiltroProveedor from "@/components/producto/filtros/ProductoSucursalFiltroProveedor.vue";
import ProductoFiltroProveedor from "@/components/producto/filtros/producto.filtro.proveedor.vue";

import FacturaCompraDetalleList from "@/components/shared/compras/factura/components/FacturaCompraDetallesList.vue";

import PedidoCompraAgregarDetalle from "@/views/compra/pedidos/pedido.compra.agregar.detalle";
import TotalesIvaFields from "@/components/compra/TotalesIvaFields.vue";
import ImpuestosAdicionalesFields from "@/components/compra/ImpuestosAdicionalesFields.vue";
import FacturaCompraSubtotal from "@/components/compra/factura/FacturaCompraSubtotal.vue";
import DatePickerDate from "@/util/DatePickerDate.vue";
import CompraDesdeArchivo from "@/components/asistente/CompraDesdeArchivo.vue";
import asistenteApi from "@/api/asistente";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "CompraFacturaEmpresaRegistrar",
  $_veeValidate: {
    validator: "new",
  },
  components: {
    ProductoSelector,
    ProductoSucursalFiltroProveedor,
    ProveedorCardSelect,
    ProductoFiltroProveedor,
    FacturaCompraDetalleList,
    PedidoCompraAgregarDetalle,
    TotalesIvaFields,
    ImpuestosAdicionalesFields,
    FacturaCompraSubtotal,
    DatePickerDate,
    CompraDesdeArchivo,
  },
  data() {
    return {
      loadingButton: false,
      // Lectura del asistente aplicada al formulario: al guardar se enseñan las asociaciones
      precarga: null,
      config: { ...COMPROBANTES.FACTURA_COMPRA },
      dialogProductoSelector: false,
      numeroFactura: null,
      fechaContable: new Date().toISOString().substr(0, 10), // Fecha inicial
      sucursal: null,
      documento: "FC",
      letra: "A",
      totalesIva: [
        { key: "27", value: 0 },
        { key: "21", value: 0 },
        { key: "10.5", value: 0 },
        { key: "5", value: 0 },
        { key: "2.5", value: 0 },
        { key: "0", value: 0 },
        { key: "Exento", value: 0 },
      ],
      percepcionIva: 0,
      percepcionIB: 0,
      netoNoGravado: 0,

      menuFecha: false,
      proveedorBusqueda: "",
      proveedores: [
        { id: 1, razonSocial: "Distribuidora ABC" },
        { id: 2, razonSocial: "Proveedora XYZ" },
      ],
      proveedorSeleccionado: null,

      productoBusqueda: "",
      productosSugeridos: [
        { id: 1, nombre: "Producto A", precio: 100 },
        { id: 2, nombre: "Producto B", precio: 250 },
      ],
      detallesFactura: [],
      headers: [
        { text: "Producto", value: "nombre" },
        { text: "Cantidad", value: "cantidad", align: "center" },
        { text: "Precio Unitario", value: "precio", align: "right" },
        { text: "Subtotal", value: "subtotal", align: "right" },
        {
          text: "Acciones",
          value: "acciones",
          sortable: false,
          align: "center",
        },
      ],

      camposImpuestos: [
        { key: "iva", label: "IVA" },
        { key: "ingresosBrutos", label: "Ingresos Brutos" },
        { key: "percepciones", label: "Percepciones" },
        { key: "descuentos", label: "Descuentos" },
        { key: "otrosCargos", label: "Otros Cargos" },
      ],
    };
  },
  computed: {
    ...mapGetters("auth", ["puedeUsarAsistente"]),
    uuidSucursal() {
      return this.$route.params.uuid_sucursal;
    },
    percepcionIBNumber() {
      return Number(this.percepcionIB) || 0;
    },
    percepcionIVANumber() {
      return Number(this.percepcionIva) || 0;
    },
    netoNoGravadoNumber() {
      return Number(this.netoNoGravado) || 0;
    },
    fechaContableFormatted() {
      return (
        this.moment(this.fechaContable).format("DD/MM/YYYY") || "DD/MM/YYYY"
      );
    },
    subtotalProductos() {
      return this.detallesFactura.reduce((acc, item) => acc + item.subtotal, 0);
    },

    totalFinal() {
      return this.subtotalProductos;
    },
  },
  methods: {
    ...mapActions("remitos", ["registrar_factura_compra", "get_remito_compra"]),

    volverAlListado() {
      this.$router.back();
    },
    onProveedorSelect(val) {
      this.proveedorSeleccionado =
        this.proveedores.find((p) => p.id === val) || null;
    },
    setProveedor(proveedor) {
      this.proveedorSeleccionado = proveedor;
    },
    // Precarga desde PDF/foto: solo llena campos. Rubro, cuenta contable, stock y
    // validaciones siguen siendo los del formulario y del backend de siempre.
    async precargarDesdeCarga(cargaId) {
      try {
        const { data } = await this.axios.get(`/productos/cargas/${cargaId}/compra`, {
          params: { sucursal: this.uuidSucursal },
        });
        await this.aplicarLectura(data);
      } catch (e) {
        const detalle = e.response && e.response.data && e.response.data.detail;
        this.$updateBus.$emit("showSnackBar", {
          show: true, color: "error", icon: "mdi-alert", timeout: 6000,
          text: detalle || "No se pudo precargar la factura del centro de carga.",
        });
      }
    },
    async aplicarLectura(lectura) {
      const c = lectura.comprobante;
      if (c.numero !== null) this.numeroFactura = String(c.numero);
      if (c.punto_venta !== null) this.sucursal = String(c.punto_venta);
      if (c.letra) this.letra = c.letra;
      if (c.fecha) this.fechaContable = c.fecha;

      try {
        const { data } = await this.axios.get(`/proveedores/${lectura.proveedor_id}`);
        this.setProveedor(data);
      } catch (e) {
        this.$updateBus.$emit("showSnackBar", { show: true, color: "error", text: "No pude cargar el proveedor.", icon: "mdi-information", timeout: 4000 });
      }

      this.detallesFactura = [];
      lectura.lineas.forEach((l) => {
        const producto = {
          ...l.producto,
          precioVenta: l.producto.precio_venta ?? l.producto.precioVenta,
          precioCosto: l.producto.precio_costo ?? l.producto.precioCosto,
          cantidadstock: l.producto.cantidad_stock ?? l.producto.cantidadstock,
          controlaStock: l.producto.controla_stock ?? l.producto.controlaStock,
        };
        const detalle = DetalleComprobanteService.agregarProducto({
          detalles: this.detallesFactura,
          producto,
          cantidad: l.cantidad,
          config: this.config,
        });
        // El precio es el de la factura, no el último costo cargado
        detalle.precioUnitario = l.precio_unitario;
        if (l.alicuota_iva !== null && l.alicuota_iva !== undefined) {
          detalle.vat_type = { description: String(l.alicuota_iva) };
        }
        detalle.normalizar();
      });

      const percepcion = (patron) =>
        (lectura.totales.percepciones || [])
          .filter((p) => patron.test(p.concepto || ""))
          .reduce((acc, p) => acc + Number(p.monto || 0), 0);
      this.percepcionIB = percepcion(/iibb|ingresos brutos|ing\.? brutos/i) || 0;
      this.percepcionIva = percepcion(/iva/i) || 0;
      this.netoNoGravado = Number(lectura.totales.neto_no_gravado || 0) + Number(lectura.totales.exento || 0);

      this.precarga = lectura;
      this.calcularTotalesIva();
      this.$updateBus.$emit("showSnackBar", {
        show: true, color: "success", icon: "mdi-creation", timeout: 5000,
        text: `Factura precargada: ${lectura.lineas.length} línea(s). Revisá y confirmá.`,
      });
    },
    // Lo que el usuario confirmó al guardar se guarda como memoria del proveedor
    aprenderAsociaciones() {
      if (!this.precarga || !this.precarga.proveedor_id) return;
      const asociaciones = this.precarga.lineas.map((l) => ({
        codigo: l.codigo,
        descripcion: l.descripcion,
        presentacion_id: l.presentacion_id,
      }));
      asistenteApi.aprenderCompra(this.precarga.proveedor_id, asociaciones).catch(() => {});
      this.precarga = null;
    },
    agregarProducto() {
      this.detallesFactura.push({
        id: Date.now(),
        nombre: `Producto ${this.detallesFactura.length + 1}`,
        cantidad: 1,
        precio: 100,
        subtotal: 100,
      });
    },

    calcularTotalesIva() {
      // Creamos un objeto para almacenar los totales acumulados por IVA
      const acumulado = this.totalesIva.reduce((acc, item) => {
        acc[item.key] = 0;
        return acc;
      }, {});

      // Iteramos sobre los detalles de la venta para calcular el total de cada IVA
      this.detallesFactura.forEach((detalle) => {
        // Verifica si existe iva_compra y su descripción; si no, usa 0
        const iva =
          detalle.producto && detalle.vat_type
            ? parseFloat(detalle.vat_type.description || 0)
            : 0;

        const subtotal =
          parseFloat(detalle.precioUnitario) * parseFloat(detalle.cantidad);

        // Acumulamos el total del IVA en el objeto de acumulado
        if (acumulado.hasOwnProperty(iva)) {
          acumulado[iva] += subtotal * (iva / 100);
        }
      });

      // Actualizamos totalesIva con los valores acumulados y redondeamos
      this.totalesIva.forEach((item) => {
        item.value = Math.round((acumulado[item.key] || 0) * 100) / 100;
      });
    },
    agregarProductoDetalle(producto, cantidad = 1) {

      DetalleComprobanteService.agregarProducto({
        detalles: this.detallesFactura,
        producto,
        cantidad,
        config: this.config,
      });
    },

    actualizarSubtotal(item) {
      item.subtotal = item.cantidad * item.precio;
    },
    eliminarItem(id) {
      this.detallesFactura = this.detallesFactura.filter(
        (item) => item.id !== id
      );
    },
    calcularTotales() {
      // Forzar recálculo si es necesario
    },
    guardarFactura() {
      this.loadingButton = true;
      this.finalizar = true;
      const totalesIvaMayoresCero = this.totalesIva
        .filter((item) => item.value > 0)
        .map((item) => ({
          key: item.key,
          value: item.value,
        }));

      var data = {
        sucursal_uuid: this.uuidSucursal,
        proveedor: this.proveedorSeleccionado,
        detalles: this.detallesFactura,
        remito: this.remitoSeleccionado,
        numero: this.numeroFactura,
        sucursal: this.sucursal,
        estaPago: false,
        percepcionIB: this.percepcionIBNumber,
        percepcionIva: this.percepcionIVANumber,
        netoNoGravado: this.netoNoGravadoNumber,
        totalesIva: totalesIvaMayoresCero,
        fechaContable: this.fechaContable,
      };

      if (this.numeroFactura !== null && !isNaN(parseInt(this.numeroFactura))) {
        data.documento = this.documento;
        data.letra = this.letra;
      }

      this.registrar_factura_compra(data)
        .then((response) => {
          this.to = true; // Si es verdadero me deja salir de la interfaz
          this.aprenderAsociaciones();

          this.volverAlListado();
          this.detallesFactura = [];
          this.proveedorSeleccionado = null;
          this.sucursal = null;
          this.numeroFactura = null;

          this.fetch_productos_mas_vendidos();
          this.loadingButton = false;
        })
        .catch((e) => {
          var message = e.response.data;
          if (e.response.data.detail) {
            message = e.response.data.detail;
          }
          this.loadingButton = false;
          this.finalizar = false;

          this.$updateBus.$emit("showSnackBar", {
            show: true,
            button: false,
            color: "error",
            text: `${message}`,
            icon: "mdi-information",
            timeout: 4000,
          });
          console.log(e);
        });
    },

    resetFormulario() {
      this.numeroFactura = null;
      this.fechaContable = new Date().toISOString().substr(0, 10);
      this.sucursal = null;
      this.totalesIva = [
        { key: "27", value: 0 },
        { key: "21", value: 0 },
        { key: "10.5", value: 0 },
        { key: "5", value: 0 },
        { key: "2.5", value: 0 },
        { key: "0", value: 0 },
        { key: "Exento", value: 0 },
      ];
      this.percepcionIva = 0;
      this.percepcionIB = 0;
      this.netoNoGravado = 0;
      this.proveedorSeleccionado = null;
      this.detallesFactura = [];
    },
    abrirDialogProducto() {
      this.dialogProductoSelector = true;
    },
    handleKeydown(e) {
      if (e.key === "Escape" && this.dialogProductoSelector) {
        e.stopPropagation(); // evita que otros lo capturen
        e.preventDefault(); // evita comportamiento por defecto
        this.dialogProductoSelector = false;
      }
      // F2 → abrir selector producto
      if (e.key === "F6") {
        e.preventDefault();
        this.abrirDialogProducto();
        return;
      }

      // F9 → guardar factura
      if (e.key === "F9") {
        e.preventDefault();

        if (this.proveedorSeleccionado && this.detallesFactura.length > 0) {
          this.guardarFactura();
        }
      }
    },
  },
  watch: {
    detallesFactura: {
      handler() {
        this.calcularTotalesIva();
      },
      deep: true,
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeydown);
    // Viene del centro de carga: la factura ya está resuelta producto por producto
    if (this.$route.query.carga) this.precargarDesdeCarga(this.$route.query.carga);
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown);
  },
};
</script>

<style scoped lang="scss">
.paper-preview {
  border: px solid #ccc;
  margin: 0 auto;
  font-family: "Courier New", Courier, monospace;
}

.factura-header {
  text-align: center;
}

.factura-datos {
  border: 1px dashed #ccc;
  padding: 16px;
  border-radius: 4px;
}

.v-divider {
  border-color: #ccc;
}
</style>
